import { INSTAGRAM_CAROUSEL_RATIO_MAX, INSTAGRAM_CAROUSEL_RATIO_MIN } from '@mondaygpt/constants'
import { useIntersectionAutoplay } from 'hooks'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ContentShape } from '../store/idea-store'
import type { Creative, InstagramMediaItem, VideoStatus } from '../types'
import { isFailedSuggestion, xPostHasText } from '../utils/suggestion-status'
import { XLogo } from './XLogo'
import { useFeedAudioStore } from '../store/feed-audio-store'
import { MuteChip } from './MuteChip'
import { BorderTravelOverlay } from '../previews/ShimmerOverlay'
import {
  FailedMediaStatusOverlay,
  GeneratingStatusText,
  toGeneratingShape,
  type GeneratingShape,
} from './GeneratingStatusText'
import {
  FAILED_REEL_MESSAGE,
  failedMediaMessage,
  isFailedVideo,
  shortFailedMediaMessage,
} from '../previews/preview-utils'

/**
 * The minimal media payload `AssetMedia` renders — a single image, a reel's
 * video (+ poster + status), or a carousel's slides, plus img alt text and a
 * terminal `failed` flag. Decoupling the primitive from the full `Creative`
 * lets every card surface (Home idea, Weekly Cockpit, Calendar) feed it without
 * hand-rolling a synthetic Creative. Use `creativeToMediaSource` to derive one
 * from a Creative; the cockpit/calendar build it directly from their DTOs.
 */
export interface MediaSource {
  /**
   * Creative id — lets the generating overlay's pill surface this creative's
   * live SocialKit phase instead of the generic rotation (was `creative.id`).
   */
  id?: string
  imageUrl?: string | null
  videoUrl?: string | null
  videoStatus?: VideoStatus
  mediaItems?: InstagramMediaItem[]
  /** Image alt text (was `creative.name`). */
  altText?: string
  /** Terminal failed-generation state (was `isFailedSuggestion(creative)`). */
  failed?: boolean
  /**
   * Text-first asset: the body of an X post with no image. Rendered as a text
   * tile instead of the "picking the right image" shimmer, which a text-only
   * tweet would otherwise show forever on every card surface.
   */
  text?: string
}

/** Derive a `MediaSource` from a full Creative (Home/Posts idea cards). */
export function creativeToMediaSource(creative: Creative | null): MediaSource {
  if (!creative) return {}
  const content = (creative.content as unknown as Record<string, unknown> | null) ?? {}
  return {
    id: creative.id,
    imageUrl: (content.imageUrl as string | null | undefined) ?? null,
    videoUrl: (content.videoUrl as string | null | undefined) ?? null,
    videoStatus: content.videoStatus as VideoStatus | undefined,
    mediaItems: content.mediaItems as InstagramMediaItem[] | undefined,
    altText: creative.name ?? '',
    failed: isFailedSuggestion(creative),
    ...(xPostHasText(creative) && { text: (content.text as string).trim() }),
  }
}

/**
 * Derive a `MediaSource` from a DTO that already carries split media fields
 * (Weekly Cockpit queue items, Calendar entries) — no synthetic Creative
 * needed. The DTO always carries `thumbnailUrl`; `videoUrl` / `mediaItems`
 * arrive only once the server stamps them, so callers downgrade the rendered
 * shape to a static image when the richer media is absent (otherwise AssetMedia
 * lands in its eternal "generating" poster state). A DTO slot whose GENERATION
 * terminally failed has no media at all — those callers pass
 * {@link FAILED_MEDIA_SOURCE} instead so the failed plate + Retry render.
 */
export function toMediaSource(media: {
  thumbnailUrl?: string | null
  videoUrl?: string | null
  mediaItems?: InstagramMediaItem[] | null
}): MediaSource {
  return {
    imageUrl: media.thumbnailUrl ?? null,
    videoUrl: media.videoUrl ?? null,
    ...(media.mediaItems ? { mediaItems: media.mediaItems } : {}),
  }
}

/**
 * The terminal-failed MediaSource for DTO-backed cards (calendar, cockpit).
 * A stable module-level reference on purpose: both cards derive their
 * mediaSource inside render/memo paths that churn with status polling, and a
 * fresh `{ failed: true }` each pass would re-render AssetMedia for nothing.
 */
export const FAILED_MEDIA_SOURCE: MediaSource = { failed: true }

interface AssetMediaProps {
  media: MediaSource | null
  shape: ContentShape
  /**
   * When true: disables video autoplay (shows poster/first frame), hides
   * carousel chevrons + dot indicators. Used in PostModal preview where the
   * card-level affordances would compete with the modal controls.
   */
  compact?: boolean
  className?: string
  /**
   * Wired by IdeaCard when this asset belongs to a Post that can be
   * regenerated. Used by the failed-video overlay (not the center play chip).
   */
  onRetry?: () => void
  /**
   * Optimistic regenerate in flight — show the generating shimmer even if the
   * creative is still in a terminal failed state until the server responds.
   */
  forceGenerating?: boolean
  /**
   * Hide the carousel's hover-revealed prev/next arrows (dots + swipe stay).
   * Used by the Weekly Cockpit tiles, which are too small to carry the arrows;
   * IdeaCard (more room) keeps them.
   */
  hideCarouselArrows?: boolean
  /**
   * Failed-plate copy length. 'short' drops the "Your credit's back." clause —
   * the compact DTO tiles (calendar week cards, cockpit queue tiles) wrap the
   * full message into a wall of text; the concession alone carries it there
   * (Matan, 2026-08-30). Default 'full' keeps the #2348 message on the bigger
   * Home/Posts cards.
   */
  failedCopy?: 'full' | 'short'
}

/** Selects the per-shape media fields from a MediaSource. */
function extractMedia(
  media: MediaSource | null,
  shape: ContentShape
): { imageUrl?: string | null; videoUrl?: string | null; mediaItems?: InstagramMediaItem[] } {
  if (!media) return {}

  if (shape === 'carousel') {
    return { mediaItems: media.mediaItems ?? [] }
  }
  if (shape === 'short_vertical_video') {
    return {
      videoUrl: media.videoUrl ?? null,
      // Some renderers store a poster as imageUrl while the video is generating.
      imageUrl: media.imageUrl ?? null,
    }
  }
  return {
    imageUrl: media.imageUrl ?? null,
  }
}

export function AssetMedia({
  media,
  shape,
  compact = false,
  className,
  onRetry,
  forceGenerating = false,
  hideCarouselArrows = false,
  failedCopy = 'full',
}: AssetMediaProps) {
  const { imageUrl, videoUrl, mediaItems } = extractMedia(media, shape)
  const wrapperClass = `relative h-full w-full overflow-hidden bg-bg-3 ${className ?? ''}`
  // On the no-media branches below, a terminally-failed suggestion shows a
  // Retry affordance; everything else falls back to the generating shimmer.
  const failed = !forceGenerating && !!media?.failed
  const noMedia = failed ? (
    <FailedAsset className={wrapperClass} shape={shape} onRetry={onRetry} copy={failedCopy} />
  ) : (
    <PlaceholderShimmer className={wrapperClass} shape={shape} creativeId={media?.id} />
  )

  if (!media) {
    return <PlaceholderShimmer className={wrapperClass} shape={shape} />
  }

  if (shape === 'carousel') {
    if (!mediaItems || mediaItems.length === 0) {
      return noMedia
    }
    return (
      <div className={wrapperClass}>
        <CarouselAsset
          items={mediaItems}
          compact={compact}
          autoplay
          showArrows={!hideCarouselArrows}
          className="h-full w-full"
        />
        {forceGenerating && (
          <GeneratingMediaOverlay
            shape={toGeneratingShape(shape)}
            radius={8}
            creativeId={media.id}
          />
        )}
      </div>
    )
  }

  if (shape === 'short_vertical_video') {
    const videoStatus = media.videoStatus
    // A reel's video lands minutes after its poster (the in-house FAL render
    // legitimately reports `videoStatus: 'ready'` before the .mp4/videoUrl is
    // persisted, and Pusher delivers the two in separate events). We key on
    // `videoUrl` first so the moment it lands we swap to <VideoAsset> (the only
    // place a working play button lives). Until then — for BOTH `generating`
    // and the transient `ready`-without-url window — we fall through to
    // <GeneratingVideoPoster>: poster + persistent loader, never a play chip.
    if (videoUrl) {
      return (
        <div className={wrapperClass}>
          <VideoAsset
            videoUrl={videoUrl}
            posterUrl={imageUrl ?? null}
            compact={compact}
            className="h-full w-full"
          />
          {forceGenerating && (
            <GeneratingMediaOverlay
              shape={toGeneratingShape(shape)}
              radius={8}
              creativeId={media.id}
            />
          )}
        </div>
      )
    }
    if (imageUrl) {
      if (!forceGenerating && isFailedVideo({ videoUrl, videoStatus })) {
        return (
          <FailedMediaStatusOverlay
            className={wrapperClass}
            posterUrl={imageUrl}
            message={FAILED_REEL_MESSAGE}
            onRetry={onRetry}
          />
        )
      }
      return (
        <GeneratingVideoPoster
          imageUrl={imageUrl}
          className={wrapperClass}
          tone="dark"
          creativeId={media.id}
        />
      )
    }
    return noMedia
  }

  // static_image (and any other shape that resolves to a single image).
  // `object-contain` (not cover) so the whole image is always visible inside
  // the card's square frame — engines like SocialKit composite bold on-image
  // text near the edges, and `object-cover` would crop a non-square master's
  // sides and clip that text. Off-ratio images letterbox against `bg-bg-3`.
  if (!imageUrl) {
    // A text-only X post is a finished asset: show the tweet, not a shimmer.
    if (media.text && !forceGenerating) {
      return <TextAsset text={media.text} className={wrapperClass} />
    }
    return noMedia
  }
  return (
    <div className={wrapperClass}>
      <img
        src={imageUrl}
        alt={media.altText ?? ''}
        className="h-full w-full object-contain"
        draggable={false}
      />
      {forceGenerating && (
        <GeneratingMediaOverlay shape={toGeneratingShape(shape)} radius={8} creativeId={media.id} />
      )}
    </div>
  )
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

/**
 * Text-first tile for an X post with no image. Taka chrome (T3 tokens), not a
 * brand-kit render: the card frames the tweet the way the feed will show it,
 * with the platform mark as a corner cue. Clamped so a long tweet never grows
 * the card past its square frame.
 */
function TextAsset({ text, className }: { text: string; className: string }) {
  return (
    // Top padding clears the card's overlay chips (shape label, status badge),
    // which the media frame sits underneath.
    <div className={`${className} flex flex-col justify-between p-4 pt-12`}>
      <p className="line-clamp-6 text-[15px] leading-snug whitespace-pre-line text-paper">{text}</p>
      <XLogo size={14} className="shrink-0 self-end text-paper-faint" />
    </div>
  )
}

interface VideoAssetProps {
  videoUrl: string
  posterUrl: string | null
  compact: boolean
  className: string
}

function VideoAsset({ videoUrl, posterUrl, compact, className }: VideoAssetProps) {
  // Autoplay when the card is fully on screen, pause when it scrolls out —
  // no manual play/pause control. Clicking the asset opens PostModal (the
  // click bubbles up to the IdeaCard); the mute chip is the only on-asset
  // control and stops propagation so it doesn't open the modal.
  const autoplayRef = useIntersectionAutoplay<HTMLVideoElement>({
    threshold: 1,
    enabled: !compact,
  })
  // Feed audio: ONE unmuted video per screen. Home/Posts can show many reels
  // (all autoplay muted); this card has sound only while it's the active one in
  // the store, and unmuting it re-mutes every other card. Default (null) = all
  // muted. The composer's single preview uses a different store, see PreviewVideo.
  const instanceId = useId() // unique per card, so duplicate videos don't sync
  const unmutedId = useFeedAudioStore(s => s.unmutedId)
  const setUnmuted = useFeedAudioStore(s => s.setUnmuted)
  const muted = unmutedId !== instanceId
  // Keep the element in sync when another card takes over the sound.
  useEffect(() => {
    const el = autoplayRef.current
    if (el) el.muted = muted
  }, [muted, autoplayRef])

  if (compact) {
    // Static frame, no autoplay — modal preview.
    return (
      <div className={className}>
        <video
          src={videoUrl}
          poster={posterUrl ?? undefined}
          className="h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
      </div>
    )
  }

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    const el = autoplayRef.current
    if (muted) {
      // Become the one unmuted card; every other card re-mutes via the store.
      setUnmuted(instanceId)
      if (el) {
        el.muted = false
        el.play().catch(() => {}) // unmute is a user gesture, so sound is allowed
      }
    } else {
      setUnmuted(null)
      if (el) el.muted = true
    }
  }

  return (
    <div className={className}>
      <video
        ref={autoplayRef}
        src={videoUrl}
        poster={posterUrl ?? undefined}
        className="h-full w-full object-cover"
        muted={muted}
        loop
        playsInline
        preload="metadata"
      />
      {/* Fixed in the bottom-right corner (`right-2 bottom-2`, sized to match
          the ••• button). It never moves — when a pre-publish card reveals its
          ••• menu in this same corner, the card lifts the ••• above the chip on
          hover (see IdeaCard) rather than displacing the chip. */}
      <MuteChip muted={muted} onToggle={handleMuteToggle} className="right-2 bottom-2 h-8 w-8" />
    </div>
  )
}

interface GeneratingVideoPosterProps {
  imageUrl: string
  className?: string
  tone?: 'dark' | 'light'
  radius?: number
  /** Surfaces the live SocialKit phase ("Rendering the reel") over the poster. */
  creativeId?: string
}

/**
 * Poster frame for reels while the video file is still pending. This is only
 * ever rendered when there's no `videoUrl` yet, so a playable video never
 * exists here — we show the poster with a persistent generating overlay and
 * NO play chip. The working play button comes from <VideoAsset> the instant
 * `videoUrl` lands. Keeping the loader up through the (potentially multi-minute)
 * `ready`-without-url window is intentional: there's nothing to play yet.
 */
export function GeneratingVideoPoster({
  imageUrl,
  className,
  tone = 'dark',
  radius = 8,
  creativeId,
}: GeneratingVideoPosterProps) {
  return (
    <div className={`relative overflow-hidden ${className ?? ''}`}>
      <img
        src={imageUrl}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <GeneratingMediaOverlay shape="video" tone={tone} radius={radius} creativeId={creativeId} />
    </div>
  )
}

interface CarouselAssetProps {
  items: InstagramMediaItem[]
  compact: boolean
  /** When false, slides don't auto-advance as the card scrolls into view. */
  autoplay: boolean
  /** When false, the hover-revealed prev/next arrows are hidden (dots stay). */
  showArrows?: boolean
  className: string
}

/** Time each slide stays before auto-advancing in the IdeaCard preview. */
const CAROUSEL_AUTOPLAY_INTERVAL_MS = 3000

/**
 * Snap-scroll carousel modeled on the existing CarouselMediaPreview, stripped
 * of the ProjectPage hover/edit affordances so it works as a card primitive.
 *
 * Auto-advances through slides only while the carousel is fully visible in
 * the viewport. Any user interaction — chevrons, dot taps, swipe, wheel —
 * pauses autoplay for the lifetime of the card and hands full control over.
 */
function CarouselAsset({
  items,
  compact,
  autoplay,
  showArrows = true,
  className,
}: CarouselAssetProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoplayPaused, setAutoplayPaused] = useState(false)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!visible) return
        const idx = slideRefs.current.findIndex(el => el === visible.target)
        if (idx >= 0) setActiveIndex(idx)
      },
      { root: scroller, threshold: [0.6] }
    )

    slideRefs.current.forEach(el => el && observer.observe(el))
    return () => observer.disconnect()
  }, [items.length])

  // Track viewport visibility on the wrapper so autoplay only ticks when the
  // card is fully on screen (matches the video autoplay rule).
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 1,
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Any direct interaction with the scroller (swipe, wheel, mouse drag)
  // permanently pauses autoplay so the user keeps control.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const onInteract = () => setAutoplayPaused(true)
    scroller.addEventListener('pointerdown', onInteract)
    scroller.addEventListener('wheel', onInteract, { passive: true })
    return () => {
      scroller.removeEventListener('pointerdown', onInteract)
      scroller.removeEventListener('wheel', onInteract)
    }
  }, [])

  // Scroll the inner snap container directly — never scrollIntoView, which
  // would propagate up and scroll the whole page if the card isn't fully
  // vertically visible.
  const scrollTo = useCallback((idx: number) => {
    const scroller = scrollerRef.current
    const target = slideRefs.current[idx]
    if (!scroller || !target) return
    scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' })
  }, [])

  // Auto-advance loop. Interval advances by one slide (wrapping back to 0
  // after the last) every CAROUSEL_AUTOPLAY_INTERVAL_MS. Reads activeIndex
  // through a ref so the interval callback always sees the latest value
  // without re-running the effect on every slide change.
  const activeIndexRef = useRef(activeIndex)
  activeIndexRef.current = activeIndex
  useEffect(() => {
    if (!autoplay || compact || autoplayPaused || !inView || items.length <= 1) return
    const interval = setInterval(() => {
      const next = (activeIndexRef.current + 1) % items.length
      scrollTo(next)
    }, CAROUSEL_AUTOPLAY_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [autoplay, compact, autoplayPaused, inView, items.length, scrollTo])

  const handleUserNav = useCallback(
    (idx: number) => {
      setAutoplayPaused(true)
      scrollTo(idx)
    },
    [scrollTo]
  )

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleUserNav(Math.max(0, activeIndex - 1))
    },
    [activeIndex, handleUserNav]
  )
  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      handleUserNav(Math.min(items.length - 1, activeIndex + 1))
    },
    [activeIndex, items.length, handleUserNav]
  )

  const aspectRatio = useMemo(() => {
    const first = items[0]?.ratio
    if (!first || !Number.isFinite(first)) return 1
    return Math.min(INSTAGRAM_CAROUSEL_RATIO_MAX, Math.max(INSTAGRAM_CAROUSEL_RATIO_MIN, first))
  }, [items])

  return (
    <div ref={wrapperRef} className={`group/carousel relative ${className}`}>
      <div
        ref={scrollerRef}
        className="flex h-full w-full snap-x snap-proximity overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ aspectRatio: String(aspectRatio) }}
      >
        {items.map((item, idx) => (
          <div
            key={item.attachmentId || `${item.url}-${idx}`}
            ref={el => {
              slideRefs.current[idx] = el
            }}
            className="relative h-full w-full flex-shrink-0 snap-start"
          >
            {item.type === 'video' ? (
              <video
                src={item.url}
                className="h-full w-full object-cover"
                muted
                loop
                playsInline
                autoPlay={autoplay && !compact && idx === activeIndex}
              />
            ) : (
              <img
                src={item.url}
                alt={item.altText ?? `Slide ${idx + 1}`}
                className="h-full w-full object-cover"
                draggable={false}
              />
            )}
          </div>
        ))}
      </div>

      {!compact && items.length > 1 && (
        <>
          {/* Prev/next arrows — hover-revealed (Pinterest-style), styled to the
              card's chip language so they read on any asset. Self-contained on
              the carousel's own `group/carousel` hover rather than the card's,
              and hidden at the respective ends. Suppressed on space-constrained
              surfaces (cockpit tiles) via `showArrows`. */}
          {showArrows && activeIndex > 0 && (
            <button
              type="button"
              onClick={handlePrev}
              aria-label="Previous slide"
              className="absolute top-1/2 left-2 z-20 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-line bg-bg-2 text-paper opacity-0 shadow-sm transition-all group-hover/carousel:opacity-100 hover:bg-bg-3 focus:opacity-100"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          {showArrows && activeIndex < items.length - 1 && (
            <button
              type="button"
              onClick={handleNext}
              aria-label="Next slide"
              className="absolute top-1/2 right-2 z-20 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-line bg-bg-2 text-paper opacity-0 shadow-sm transition-all group-hover/carousel:opacity-100 hover:bg-bg-3 focus:opacity-100"
            >
              <ChevronRight size={16} />
            </button>
          )}
          {/* Slide dots — Instagram-style indicator centered at the bottom,
              overlaid on the asset. Always visible (they're the at-rest signal
              that this post is a multi-page carousel), so they sit above the
              card's hover scrims (z-20). Tappable to jump; soft drop-shadow
              keeps them legible on bright assets without a background plate.
              The `h-8` band vertically centers the dots on the same row as the
              card's bottom-left "Visit" / bottom-right ••• buttons (all 32px
              tall at `bottom-2`) so the three controls line up. */}
          <div className="absolute bottom-2 left-1/2 z-20 flex h-8 -translate-x-1/2 items-center gap-1.5">
            {items.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  handleUserNav(idx)
                }}
                aria-label={`Go to slide ${idx + 1}`}
                aria-current={idx === activeIndex}
                className={`h-1.5 w-1.5 cursor-pointer rounded-full shadow-[0_0_2px_rgba(0,0,0,0.55)] transition-all duration-200 ${
                  idx === activeIndex ? 'bg-white' : 'bg-white/45 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

interface FailedAssetProps {
  className: string
  shape: ContentShape
  /** Wired by IdeaCard to regenerate the Post. Absent in read-only previews. */
  onRetry?: () => void
  /** 'short' drops the refund clause on compact tiles (see AssetMediaProps). */
  copy?: 'full' | 'short'
}

const FAILED_SHAPE_LABEL: Partial<Record<ContentShape, string>> = {
  static_image: 'image',
  short_vertical_video: 'reel',
  carousel: 'carousel',
}

/**
 * Terminal failed state for a suggestion card whose generation produced no
 * media. Replaces the otherwise never-ending PlaceholderShimmer so the user
 * gets an explanation + a Retry instead of an eternal loader.
 */
function FailedAsset({ className, shape, onRetry, copy = 'full' }: FailedAssetProps) {
  const label = FAILED_SHAPE_LABEL[shape] ?? 'post'

  return (
    <FailedMediaStatusOverlay
      className={className}
      message={copy === 'short' ? shortFailedMediaMessage(label) : failedMediaMessage(label)}
      onRetry={onRetry}
    />
  )
}

interface PlaceholderShimmerProps {
  className: string
  shape?: ContentShape
  /** Surfaces the live SocialKit phase on the pill while this creative generates. */
  creativeId?: string
}

function PlaceholderShimmer({ className, shape, creativeId }: PlaceholderShimmerProps) {
  return (
    <div className={className}>
      <GeneratingMediaOverlay
        shape={shape ? toGeneratingShape(shape) : undefined}
        radius={8}
        creativeId={creativeId}
      />
    </div>
  )
}

interface GeneratingMediaOverlayProps {
  /** When set, cycles shape-specific progress copy over the media area. */
  shape?: GeneratingShape
  tone?: 'dark' | 'light'
  /** Match the parent media container's corner radius (IdeaCard uses 8). */
  radius?: number
  speed?: number
  /** When set, the pill shows this creative's live SocialKit phase instead of the rotation. */
  creativeId?: string
}

export function GeneratingMediaOverlay({
  shape,
  tone = 'dark',
  radius = 8,
  speed,
  creativeId,
}: GeneratingMediaOverlayProps) {
  return (
    <>
      <BorderTravelOverlay radius={radius} speed={speed} />
      {shape && <GeneratingStatusText shape={shape} isActive tone={tone} creativeId={creativeId} />}
    </>
  )
}
